// Browser-side access to the vendored CTG viewer (SOUP, docs/soup/fhrviewer.md): served verbatim from
// public/vendor/fhrviewer and loaded at runtime, never bundled nor modified. Shared by the course embeds and the
// back-office trace editor.
export type ViewerMarker = [number, string]

export type FhrViewerInstance = {
  loadBuffer: (buffer: ArrayBuffer, ext: string, layout?: { headerBytes?: 4 | 8; bytesPerSample?: number }) => unknown
  loadMarkers: (text: string) => unknown
  getMarkers: () => ViewerMarker[]
  scrollTo: (epochOrFraction: number) => unknown
  setHeight: (px: number) => unknown
  on: (event: string, cb: (detail: Record<string, unknown>) => void) => unknown
  // Read-only view state (left edge epoch, window length in seconds): used to crop "from the current view".
  graph: { time: number; winlength: number; signals: { start: number; lastTime: number } }
}
type FhrViewerModule = { FHRViewer: new (host: HTMLElement, opts: Record<string, unknown>) => FhrViewerInstance }

const viewerBaseUrl = `${process.env.NEXT_PUBLIC_BASE_PATH ?? ''}/vendor/fhrviewer`
let modulePromise: Promise<FhrViewerModule> | null = null

function ensureStylesheet() {
  if (document.querySelector('link[data-fhrviewer-css]')) return
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = `${viewerBaseUrl}/fhrviewer.css`
  link.dataset.fhrviewerCss = ''
  document.head.appendChild(link)
}

export async function createFhrViewer(host: HTMLElement, opts: Record<string, unknown>): Promise<FhrViewerInstance> {
  ensureStylesheet()
  const moduleUrl = `${viewerBaseUrl}/fhrviewer.js`
  modulePromise ??= import(/* webpackIgnore: true */ /* turbopackIgnore: true */ moduleUrl) as Promise<FhrViewerModule>
  const { FHRViewer } = await modulePromise
  return new FHRViewer(host, opts)
}

// The viewer has no destroy(): emptying the host detaches its DOM (its ResizeObserver watches a removed node).
export function disposeFhrViewerHost(host: HTMLElement | null) {
  if (host) host.innerHTML = ''
}
